import { surfaceKeyToInt } from '../elevation/surfaces';
import { pathDetailsForUpload } from './segmentPathDetails';
import { cumulativeDistancesMeters } from './turnInstructions';
import type { LineStringFeature } from './LineStringFeature';

/**
 * One line string as `Ptv2LineStringAttributes` saves it: geometry, one surface per edge,
 * distances, turns, profile and path details. Shared by the planner's save and the Import files snap, so a
 * snapped imported day is stored exactly like a segment the planner routed.
 *
 * Null for a line under two points, which the backend can't store as geometry.
 */
export function lineStringSaveAttributes(lineStringFeature: LineStringFeature) {
  const coordinates = lineStringFeature.lineStringGeometry.coordinates;
  if (!coordinates || coordinates.length < 2) return null;

  // One per edge, N points carrying N − 1, as the apps and the server's breakdown read them.
  const surfaces: number[] = new Array(Math.max(coordinates.length - 1, 0)).fill(0); // Default to 0 (unknown)
  for (const [startIdx, endIdx, surfaceType] of lineStringFeature.lineStringProperties.extras?.surface?.values ?? []) {
    // Canonical string→integer surface map (matches LineStringPropertiesSurface.surfaceIntToString
    // and the GraphQL wire contract). `missing` maps to 0 (unknown).
    const surfaceInt = typeof surfaceType === 'string' ? (surfaceKeyToInt[surfaceType] ?? 0) : (surfaceType as number);
    for (let i = startIdx as number; i < (endIdx as number) && i < surfaces.length; i++) {
      surfaces[i] = surfaceInt;
    }
  }

  // The same convention the turns were positioned with, so a turn and the geometry it
  // belongs to are measured on one scale — see `turnInstructions`.
  const distances = cumulativeDistancesMeters(coordinates);
  const instructions = lineStringFeature.lineStringProperties.instructions ?? [];
  const profile = lineStringFeature.lineStringProperties.profile;
  // Left out when there are none, so the server keeps what it has for a segment whose
  // points didn't change instead of being told it has no details.
  const pathDetails = pathDetailsForUpload(lineStringFeature.lineStringProperties.pathDetails, coordinates.length);

  return {
    geometry: lineStringFeature.toWKT(distances),
    surfaces,
    distances,
    // Turns ride along with the segment GraphHopper snapped them for, so a recording
    // can be guided along it without matching anything back to the route later.
    instructions,
    ...(profile ? { profile } : {}),
    // GraphHopper's path details (SAC rating, road class, slope, ...), as the apps send them.
    ...(pathDetails ? { pathDetails } : {})
  };
}
